import { formatLocaleNumber } from "@/utils/formatUtils";
import {
  getDatePickerFormat,
  getDateTimePickerFormat,
  selectFilterFunction,
  selectTagsFilterFunction,
} from "@/utils/formUtils";
import { dateToIsoDateString, dateToUtcIsoDateTimeString } from "@/utils/utils";
import { DatePickerProps, GetProp, TableProps } from "antd";
import { FormItemProps } from "antd/es/form/FormItem";
import { PaginationConfig } from "antd/lib/pagination";
import { SelectProps } from "antd/lib/select";
import { TablePaginationConfig } from "antd/lib/table";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import t from "../../app/i18n";

export const paginationStandardProps: Omit<PaginationConfig, "position"> = {
  showTotal: (total, [from, to]) =>
    t("common.pagination", {
      from: formatLocaleNumber(from),
      to: formatLocaleNumber(to),
      total: formatLocaleNumber(total),
    }),
  showQuickJumper: true,
  showSizeChanger: false,
  itemRender: (page, type, originalElement) => {
    if (type === "page") {
      return formatLocaleNumber(page);
    }
    return originalElement;
  },
};

export const paginationTableProps: TablePaginationConfig = {
  ...paginationStandardProps,
  placement: ["topEnd", "bottomEnd"],
};

export const tableStandardProps = <T = any>(
  stripped: boolean = true,
  noHeaderBackground: boolean = false,
  additionalClassName?: string,
): Partial<TableProps<T>> => ({
  size: "small",
  className: classNames(
    "standard-table",
    { stripped: stripped },
    { "no-header-background": noHeaderBackground },
    additionalClassName,
  ),
  rowKey: "id",
  rowClassName: (_: any, index: number) => {
    if (!stripped) {
      return "";
    }
    return index % 2 === 0 ? "table-row-light" : "table-row-dark";
  },
  locale: { emptyText: t("common.noData") },
  scroll: { x: "max-content" },
});

type SharedPickerProps = Pick<DatePickerProps, "format" | "placeholder" | "allowClear" | "style">;

export const datePickerStandardProps: SharedPickerProps = {
  format: getDatePickerFormat(),
  placeholder: "",
  allowClear: false,
  style: { width: "100%" },
};

export const datePickerClearableProps: SharedPickerProps = {
  ...datePickerStandardProps,
  allowClear: true,
};

export const datePickerFormItemProps: Pick<FormItemProps, "getValueProps" | "normalize"> = {
  getValueProps: (value: any) => ({ value: value && dayjs(value) }),
  normalize: (value: any) => (value ? dateToIsoDateString(value) : undefined),
};

export const rangePickerFormItemProps = (
  picker: GetProp<DatePickerProps, "picker"> = "date",
): Pick<FormItemProps, "getValueProps" | "normalize"> => {
  return {
    getValueProps: (value?: (string | number | undefined)[]) => {
      if (value && Array.isArray(value)) {
        if (picker === "year") {
          return { value: value.map((item) => (item ? dayjs(`${item}-01-01`) : undefined)) };
        }

        return { value: value.map((item) => (item ? dayjs(item) : undefined)) };
      }

      return { value: undefined };
    },
    normalize: (value?: [Dayjs | null, Dayjs | null]) => {
      let result: Dayjs[] = [];

      if (value && Array.isArray(value)) {
        switch (picker) {
          case "year":
            return value.map((item) => item?.year());
          case "quarter": {
            const startQuarter = value[0]?.startOf("month");
            const quarterEndMonth = value[1] ? Math.ceil((value[1].month() + 1) / 3) * 3 : undefined;
            const endQuarter = quarterEndMonth ? value[1]?.month(quarterEndMonth - 1).endOf("month") : undefined;

            if (startQuarter && endQuarter) {
              result.push(startQuarter);
              result.push(endQuarter.isBefore(dayjs()) ? endQuarter : dayjs());
            }
            break;
          }
          case "month": {
            const startMonth = value[0]?.startOf("month");
            const endMonth = value[1]?.endOf("month");

            if (startMonth && endMonth) {
              result.push(startMonth);
              result.push(endMonth.isBefore(dayjs()) ? endMonth : dayjs());
            }
            break;
          }
          case "week": {
            const startWeek = value[0]?.startOf("week");
            const endWeek = value[1]?.endOf("week");

            if (startWeek && endWeek) {
              result.push(startWeek);
              result.push(endWeek.isBefore(dayjs()) ? endWeek : dayjs());
            }
            break;
          }
          case "time":
            return value.map((item) => (item ? dateToUtcIsoDateTimeString(item) : undefined));
          default:
            result = value.filter((item) => !!item);
            break;
        }

        return result.map((item) => dateToIsoDateString(item));
      }

      return undefined;
    },
  };
};

export const quillFormItemProps: Pick<FormItemProps, "getValueProps" | "normalize"> = {
  normalize: (value) => {
    if (value === "<p><br></p>") {
      return undefined;
    }

    return value;
  },
  getValueProps: (value) => {
    if (value === "<p><br></p>") {
      return { value: undefined };
    }

    return { value: value };
  },
};

const dateTimePickerStandardProps: SharedPickerProps = {
  format: getDateTimePickerFormat(),
  placeholder: "",
  allowClear: false,
};

export const dateTimePickerClearableProps: SharedPickerProps = {
  ...dateTimePickerStandardProps,
  allowClear: true,
};

export const selectStandardProps: SelectProps = {
  showSearch: { filterOption: selectFilterFunction },
  popupMatchSelectWidth: false,
};

export const selectTagsStandardProps = (translationsMap: Map<string | number, string>): SelectProps => ({
  showSearch: { filterOption: (inputValue, option) => selectTagsFilterFunction(inputValue, translationsMap, option) },
  popupMatchSelectWidth: false,
});

export const quillEditorStandardProps = {
  theme: "snow",
  modules: {
    toolbar: [
      [{ size: ["small", false, "large"] }],
      ["bold", "italic", "underline", "strike", { color: [] }],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link", "clean"],
    ],
  },
  formats: [
    "header",
    "size",
    "code",
    "code-block",
    "blockquote",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "list",
    "indent",
    "link",
  ],
};
